<template>
  <v-expansion-panel class="mt-3" value="0">
    <v-expansion-panel-header color="accent">
      <div class="float-left subtitle-1">
        <v-icon color="primary" class="iconFix mr-3">mdi-map-marker-radius</v-icon>
        {{ $vuetify.lang.t("$vuetify.company.gln") }}
      </div>
    </v-expansion-panel-header>
    <v-expansion-panel-content class="caption">
      <v-row class="mb-3">
        <v-col cols="12">
          <v-btn small color="primary" @click="openCreate">
            + {{ $vuetify.lang.t("$vuetify.action.add") }}
          </v-btn>
        </v-col>
      </v-row>

      <v-progress-linear
        v-if="isLoading"
        indeterminate
        color="primary"
        class="mb-3"
      ></v-progress-linear>

      <v-alert v-if="!isLoading && !items.length" outlined type="info" dense>
        {{ $vuetify.lang.t("$vuetify.gln.empty") }}
      </v-alert>

      <div
        v-else
        class="gln-records"
      >
        <v-card
          v-for="item in items"
          :key="item.id"
          outlined
          class="gln-record"
        >
          <v-card-text class="py-3 px-4">
            <div class="gln-record__top">
              <div class="pr-4">
                <div class="subtitle-2 font-weight-medium">
                  {{ localizedName(item) }}
                </div>
                <div class="caption grey--text text--darken-1 mt-1">
                  {{ recordHint(item) }}
                </div>
              </div>

              <GlnActions
                :gln="item"
                :loadingId="actionLoadingId"
                @edit="openEdit(item)"
                @remove="openDelete(item)"
              />
            </div>

            <div class="gln-record__chips mt-3">
              <v-chip small outlined color="primary" label>
                {{ glnTypeText(item.gln_type) }}
              </v-chip>
              <v-chip small outlined label>
                {{ item.gln || "-" }}
              </v-chip>
              <v-chip
                small
                label
                :color="statusColor(item.status)"
                text-color="white"
              >
                {{ statusText(item.status) }}
              </v-chip>
            </div>

            <div class="caption grey--text text--darken-1 mt-3">
              {{ item.insert_date || "-" }}
            </div>
          </v-card-text>
        </v-card>
      </div>

      <AddGln
        :popupAddGln="dialogAddGln"
        :orgID="companyId"
        :glnData="selectedGln"
        @closePopupAddGln="closeAddDialog"
        @created="onSaved"
        @updated="onSaved"
      />

      <RemoveGln
        :removeGlnPopup="dialogRemoveGln"
        :gln="selectedGln"
        @closeRemoveGlnPopup="closeRemoveDialog"
        @reload="onDeleted"
      />
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import Api from "@/services/api";
import { filterVisibleGlns } from "@/helpers/gln";
import GlnActions from "@/components/manager/GlnActions";
import AddGln from "@/components/manager/popup/AddGln";
import RemoveGln from "@/components/manager/popup/RemoveGln";

export default {
  components: {
    GlnActions,
    AddGln,
    RemoveGln,
  },
  props: {
    companyId: {
      type: [Number, String],
      required: true,
    },
  },
  data: function() {
    return {
      dialogAddGln: false,
      dialogRemoveGln: false,
      isLoading: false,
      items: [],
      selectedGln: null,
      actionLoadingId: null,
    };
  },
  watch: {
    companyId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.loadList();
        }
      },
    },
  },
  methods: {
    openCreate() {
      this.selectedGln = null;
      this.dialogAddGln = true;
    },
    openEdit(item) {
      this.actionLoadingId = item.id;
      Api.gln("get", item.id)
        .then(({ data }) => {
          this.selectedGln = data;
          this.dialogAddGln = true;
        })
        .finally(() => {
          this.actionLoadingId = null;
        });
    },
    openDelete(item) {
      this.selectedGln = item;
      this.dialogRemoveGln = true;
    },
    closeAddDialog() {
      this.dialogAddGln = false;
      this.selectedGln = null;
    },
    closeRemoveDialog() {
      this.dialogRemoveGln = false;
      this.selectedGln = null;
    },
    localizedName(item) {
      return item.name[this.$i18n.locale] || item.name.en || item.name.ka || item.gln || "-";
    },
    recordHint(item) {
      if (item.digital_address) {
        return item.digital_address;
      }

      if (item.fixed_address_locality) {
        const locality =
          item.fixed_address_locality[this.$i18n.locale] ||
          item.fixed_address_locality.en ||
          item.fixed_address_locality.ka;
        const postal =
          item.fixed_address_postal_name &&
          (item.fixed_address_postal_name[this.$i18n.locale] ||
            item.fixed_address_postal_name.en ||
            item.fixed_address_postal_name.ka);

        return [locality, postal].filter(Boolean).join(", ") || "-";
      }

      if (item.contact_email) {
        return item.contact_email;
      }

      return item.gln || "-";
    },
    glnTypeText(type) {
      const item = this.$vuetify.lang
        .t("$vuetify.gln.types")
        .find((option) => option.value === type);

      return item ? item.text : type;
    },
    statusText(status) {
      if (!status) {
        return "-";
      }

      return this.$i18n.messages[this.$i18n.locale].$vuetify.status[status] || status;
    },
    statusColor(status) {
      if (status === "active" || status === "confirmed") {
        return "success";
      }

      if (status === "review" || status === "unconfirmed") {
        return "warning";
      }

      if (status === "excluded") {
        return "error";
      }

      return "grey";
    },
    loadList() {
      this.isLoading = true;
      Api.gln("list", "company_id=" + this.companyId)
        .then(({ data }) => {
          this.items = filterVisibleGlns(data.items);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    onSaved(item) {
      this.loadList();
      this.$emit("refresh-company", item);
    },
    onDeleted(item) {
      this.loadList();
      this.$emit("refresh-company", item);
    },
  },
};
</script>

<style scoped>
.gln-records {
  max-height: 350px;
  overflow-y: auto;
  padding-right: 6px;
}

.gln-record + .gln-record {
  margin-top: 12px;
}

.gln-record__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.gln-record__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
</style>
